import { motion } from 'framer-motion'
import {
    AcademicCapIcon,
    BookOpenIcon,
    UserGroupIcon,
    QuestionMarkCircleIcon
} from '@heroicons/react/24/outline'

const StatsSection = ({ stats }) => {
    const statItems = [
        {
            id: 'universities',
            label: 'Universities',
            value: stats?.universities || 0,
            icon: AcademicCapIcon,
            color: 'text-primary-600',
            bgColor: 'bg-primary-100',
        },
        {
            id: 'courses',
            label: 'Courses',
            value: stats?.courses || 0,
            icon: BookOpenIcon,
            color: 'text-secondary-600',
            bgColor: 'bg-secondary-100',
        },
        {
            id: 'enquiries',
            label: 'Students Helped',
            value: stats?.enquiries || 0,
            icon: UserGroupIcon,
            color: 'text-accent-600',
            bgColor: 'bg-accent-100',
        },
        {
            id: 'faqs',
            label: 'FAQs Answered',
            value: stats?.faqs || 0,
            icon: QuestionMarkCircleIcon,
            color: 'text-gold-600',
            bgColor: 'bg-gold-100',
        },
    ]

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2,
            },
        },
    }

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.6,
                ease: 'easeOut',
            },
        },
    }

    return (
        <section className="py-16 lg:py-24 bg-gray-50">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-12 lg:mb-16"
                >
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                        Trusted by Thousands of Students
                    </h2>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                        Join our growing community of successful professionals who have transformed their careers through quality education
                    </p>
                </motion.div>

                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    className="grid grid-cols-2 lg:grid-cols-4 gap-8"
                >
                    {statItems.map((item) => {
                        const Icon = item.icon
                        return (
                            <motion.div
                                key={item.id}
                                variants={itemVariants}
                                className="text-center group"
                            >
                                <div className="relative">
                                    {/* Background Circle */}
                                    <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-gray-100 rounded-full transform scale-110 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                                    {/* Icon Container */}
                                    <div className={`relative w-16 h-16 ${item.bgColor} rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300`}>
                                        <Icon className={`w-8 h-8 ${item.color}`} />
                                    </div>
                                </div>

                                {/* Number */}
                                <motion.div
                                    initial={{ scale: 0 }}
                                    whileInView={{ scale: 1 }}
                                    viewport={{ once: true }}
                                    transition={{
                                        duration: 0.6,
                                        delay: 0.3,
                                        type: 'spring',
                                        stiffness: 200
                                    }}
                                    className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-2"
                                >
                                    {item.value.toLocaleString()}+
                                </motion.div>

                                {/* Label */}
                                <div className="text-sm sm:text-base text-gray-600 font-medium">
                                    {item.label}
                                </div>
                            </motion.div>
                        )
                    })}
                </motion.div>

                {/* Additional Stats Row */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    className="mt-12 lg:mt-16 text-center"
                >
                    <div className="inline-flex flex-col sm:flex-row items-center cursor-default space-y-6 sm:space-y-0 sm:space-x-8 bg-gray-50 rounded-2xl px-8 py-6 w-full sm:w-auto">
                        <div className="text-center">
                            <div className="text-2xl font-bold text-primary-600">98%</div>
                            <div className="text-sm text-gray-600">Success Rate</div>
                        </div>
                        <div className="w-full h-px sm:w-px sm:h-12 bg-gray-300"></div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-primary-600">24/7</div>
                            <div className="text-sm text-gray-600">Support</div>
                        </div>
                        <div className="w-full h-px sm:w-px sm:h-12 bg-gray-300"></div>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-primary-600">100%</div>
                            <div className="text-sm text-gray-600">UGC Recognized</div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    )
}

export default StatsSection
